import { AudioPlayer } from '@mediagrid/capacitor-native-audio';
import { Capacitor } from '@capacitor/core';
import logger from '../utils/logger';

/**
 * Sound types used in the round timer
 * Unified naming convention shared with tvOS MaruneTV app
 */
export type SoundType = 'bell' | 'warning' | 'beep' | 'complete';

/**
 * Sound file mappings
 * These match the tvOS MaruneTV app for consistency
 * - bell: Round start/end, rest transitions
 * - warning: 10 seconds warning
 * - beep: Countdown beeps (3-2-1)
 * - complete: Workout finished
 */
const SOUNDS: Record<SoundType, string> = {
  bell: 'assets/sounds/bell.mp3',
  warning: 'assets/sounds/warning.mp3',
  beep: 'assets/sounds/beep.mp3',
  complete: 'assets/sounds/complete.mp3'
};

/**
 * Service for managing timer sound playback
 * Handles both native (iOS/Android) and web audio playback
 */
class SoundService {
  private isNative: boolean;
  private preloaded = new Set<SoundType>();
  private initialized = false;
  private htmlAudioCache: Record<string, HTMLAudioElement> = {};

  constructor() {
    this.isNative = Capacitor.isNativePlatform();
  }

  /**
   * Initialize the sound service
   * Preloads all sounds on native platforms
   */
  async initialize(): Promise<void> {
    if (this.initialized) {
      logger.info('[SoundService] Already initialized');
      return;
    }

    logger.info(`[SoundService] Initializing (platform: ${this.isNative ? 'native' : 'web'})`);

    if (!this.isNative) {
      // On web, preload HTML5 audio elements
      for (const [id, path] of Object.entries(SOUNDS)) {
        try {
          const audio = new Audio(path);
          audio.preload = 'auto';
          this.htmlAudioCache[id] = audio;
          logger.info(`[SoundService] Preloaded HTML5 audio: ${id}`);
        } catch (error) {
          logger.error(`[SoundService] Failed to preload HTML5 audio ${id}:`, error);
        }
      }
      this.initialized = true;
      return;
    }

    // On native platforms, use AudioPlayer plugin
    try {
      for (const [id, path] of Object.entries(SOUNDS)) {
        try {
          // Create audio source
          await AudioPlayer.create({
            audioId: id,
            audioSource: path,
            friendlyTitle: `Timer ${id}`,
            useForNotification: false, // Timer sounds shouldn't use notification
            isBackgroundMusic: false,
            loop: false
          });

          // Initialize (buffer) the audio
          await AudioPlayer.initialize({ audioId: id });

          this.preloaded.add(id as SoundType);
          logger.info(`[SoundService] Preloaded native audio: ${id}`);
        } catch (error) {
          logger.error(`[SoundService] Failed to preload ${id}:`, error);
          // Continue with other sounds even if one fails
        }
      }
      this.initialized = true;
      logger.info('[SoundService] Native audio initialization complete');
    } catch (error) {
      logger.error('[SoundService] Native audio initialization failed:', error);
      throw error;
    }
  }

  /**
   * Play a sound
   * @param soundType - Type of sound to play
   */
  async play(soundType: SoundType): Promise<void> {
    if (!this.initialized) {
      logger.warn('[SoundService] Not initialized, attempting to initialize...');
      try {
        await this.initialize();
      } catch (error) {
        logger.error('[SoundService] Failed to initialize:', error);
        return;
      }
    }

    if (!this.isNative) {
      // Web platform - use HTML5 audio
      try {
        const audio = this.htmlAudioCache[soundType];
        if (audio) {
          // Reset to beginning in case it's already playing
          audio.currentTime = 0;
          await audio.play();
          logger.info(`[SoundService] Played HTML5 audio: ${soundType}`);
        } else {
          logger.warn(`[SoundService] Sound not cached: ${soundType}`);
        }
      } catch (error) {
        logger.error(`[SoundService] Failed to play HTML5 audio ${soundType}:`, error);
      }
      return;
    }

    // Native platform - use AudioPlayer plugin
    if (!this.preloaded.has(soundType)) {
      logger.warn(`[SoundService] Sound not preloaded: ${soundType}`);
      return;
    }

    try {
      await AudioPlayer.play({ audioId: soundType });
      logger.info(`[SoundService] Played native audio: ${soundType}`);
    } catch (error) {
      logger.error(`[SoundService] Failed to play native audio ${soundType}:`, error);
    }
  }

  /**
   * Stop a sound
   * @param soundType - Type of sound to stop
   */
  async stop(soundType: SoundType): Promise<void> {
    if (!this.initialized) return;

    if (!this.isNative) {
      // Web platform
      const audio = this.htmlAudioCache[soundType];
      if (audio) {
        audio.pause();
        audio.currentTime = 0;
      }
      return;
    }

    // Native platform
    if (!this.preloaded.has(soundType)) return;

    try {
      await AudioPlayer.stop({ audioId: soundType });
      logger.info(`[SoundService] Stopped: ${soundType}`);
    } catch (error) {
      logger.error(`[SoundService] Failed to stop ${soundType}:`, error);
    }
  }

  /**
   * Check if sound service is initialized
   */
  isInitialized(): boolean {
    return this.initialized;
  }

  /**
   * Clean up resources
   * Unloads all sounds on native platforms
   */
  async cleanup(): Promise<void> {
    if (!this.initialized) return;

    logger.info('[SoundService] Cleaning up');

    if (!this.isNative) {
      // Web platform - clear audio cache
      Object.values(this.htmlAudioCache).forEach(audio => {
        audio.pause();
        audio.src = '';
      });
      this.htmlAudioCache = {};
      this.initialized = false;
      return;
    }

    // Native platform - destroy all sounds
    for (const soundType of this.preloaded) {
      try {
        await AudioPlayer.destroy({ audioId: soundType });
        logger.info(`[SoundService] Destroyed: ${soundType}`);
      } catch (error) {
        logger.error(`[SoundService] Failed to destroy ${soundType}:`, error);
      }
    }

    this.preloaded.clear();
    this.initialized = false;
    logger.info('[SoundService] Cleanup complete');
  }
}

// Export singleton instance
export const soundService = new SoundService();
